/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Exact,
  Platform,
  PostType,
  useCancelPostMutation,
  UserPostsQuery,
} from 'graphql/generated/graphql'
import Keyword from 'Keyword/Keyword'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import React, { useState } from 'react'
import { format } from 'date-fns'
import { Alert, Button, Modal } from '@mui/material'
import Spinner from 'components/Spinner/Spinner'
import { ApolloQueryResult } from '@apollo/client'
import { Colors } from 'constants/colors'

interface PostInfoProps {
  data: {
    __typename?: 'Post' | undefined
    id: number
    platform: Platform
    postDate: string
    caption: string
    imageMediaURLs: Array<string | null>
    videoMediaURLs: Array<string | null>
    postType: PostType
    accountName: string
  } | null
  scheduled: boolean
  refetch: (
    variables?:
      | Partial<
          Exact<{
            [key: string]: never
          }>
        >
      | undefined
  ) => Promise<ApolloQueryResult<UserPostsQuery>>
}

export default function PostInfo({
  data,
  scheduled,
  refetch,
}: PostInfoProps): JSX.Element {
  const [openModal, setOpenModal] = useState(false)
  const [cancelError, setCancelError] = useState(false)
  const [cancelPost, { loading }] = useCancelPostMutation({
    variables: {
      postId: data!.id,
    },
  })
  return (
    <div
      style={{
        backgroundColor: Colors.backgroundDark,
        padding: '10px',
        borderRadius: '10px',
      }}
    >
      {cancelError && <Alert severity="error">Failed to cancel post</Alert>}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '5px 0',
        }}
      >
        <Keyword platform={data?.platform} text={data?.platform} />
        {scheduled && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setOpenModal(true)}
          >
            Cancel
          </Button>
        )}
      </div>
      <div>
        {/* TODO: Figure out what you want to do with carousels */}
        {data?.postType === 'Image' ? (
          <img
            src={data.imageMediaURLs[0] ?? ''}
            width={300}
            height={180}
            style={{ borderRadius: '10px', objectFit: 'cover' }}
          />
        ) : (
          <video
            src={data?.videoMediaURLs[0] ?? ''}
            width={300}
            height={180}
            controls
          />
        )}
        <p>{data?.caption}</p>
      </div>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <AccountCircleIcon />
          <span>{data?.accountName}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <AccessTimeIcon />
          <span>
            {format(new Date(data?.postDate ?? ''), 'MM-dd-yy HH:mm')}
          </span>
        </div>
      </div>
      <Modal open={openModal}>
        {loading ? (
          <div
            style={{
              position: 'absolute' as 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <Spinner />
          </div>
        ) : (
          <div
            style={{
              backgroundColor: Colors.backgroundDark,
              padding: '20px 30px',
              width: '100%',
              maxWidth: '500px',
              minWidth: '250px',
              textAlign: 'center',
              borderRadius: '10px',
              position: 'absolute' as 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#fff',
            }}
          >
            <p>Are you sure you want to cancel the post?</p>
            <div
              style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
            >
              <Button
                variant="outlined"
                color="success"
                onClick={() => {
                  cancelPost()
                    .then((res) => {
                      if (res.data?.cancelPost) {
                        setOpenModal(false)
                        setCancelError(false)
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        refetch()
                      } else {
                        setCancelError(true)
                      }
                    })
                    .catch((e) => {
                      setCancelError(true)
                      console.log(e)
                    })
                }}
              >
                Yes
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setOpenModal(false)}
              >
                No
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
